const EntryService = require('../services/EntryService').default;
const getSpace = require('./contentful/get-space');
const transformer = require('./transformers/openGraph');
const linkResolve = require('./contentful/link-resolve');
const processError = require('./contentful/error-process');

let entryService = null;

const gettingEntryService = () => {
  if (!entryService) {
    entryService = getSpace().then((space) => {
      return new EntryService(space, transformer);
    });
  }

  return entryService;
};

module.exports = {
  getById(...parameters) {
    return gettingEntryService().then((service) => service.getById(...parameters));
  },

  save(og, depth = 0) {
    if (!og.id) {
      return gettingEntryService().then((service) => service.create('openGraph', og, depth));
    }
    return gettingEntryService().then((service) => service.save(og, depth));
  },

  publish(...parameters) {
    return gettingEntryService().then((service) => service.publish(...parameters));
  },

  unpublish(...parameters) {
    return gettingEntryService().then((service) => service.unpublish(...parameters));
  },

  archive(...parameters) {
    return gettingEntryService().then((service) => service.archive(...parameters));
  },

  unarchive(...parameters) {
    return gettingEntryService().then((service) => service.unarchive(...parameters));
  },

  getAll(depth = 0) {
    return getSpace().then((space) => {
      return space.getEntries({
        content_type: 'openGraph',
        order: '-fields.motorforumOnly,fields.priority,-sys.createdAt',
        limit: 999
      }).catch(
        processError
      ).then((response) => {
        const data = response.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  getByTargetId(targetId, depth = 0) {
    if (!targetId) {
      throw new Error('Expected non-falsy target ID.');
    }

    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'openGraph',
        'fields.target.sys.id': targetId,
        'order': '-fields.motorforumOnly,-fields.make.sys.id,fields.priority,-sys.createdAt',
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  getByMakeId(makeId, depth = 0) {
    if (!makeId) {
      throw new Error('Expected non-falsy make ID.');
    }

    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'openGraph',
        'fields.make.sys.id': makeId,
        'order': '-fields.motorforumOnly,-fields.make.sys.id,fields.priority,-sys.createdAt',
        'fields.target[exists]': false,
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  /*
    Get all default/fallback opengraphs for this dealer.
    Those are opengraphs that are not assigned to any dealers, or assigned to this one,
    and have no target and is not motorforumOnly.
  */
  getDefaultsByMakeId(makeId, depth = 0) {
    if (!makeId) {
      throw new Error('Expected non-falsy make ID.');
    }

    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'openGraph',
        'fields.motorforumOnly[ne]': true,
        'fields.target[exists]': false,
        'order': '-fields.make.sys.id,fields.priority,-sys.createdAt',
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();
        data.items = data.items.filter((og) => {
          if (og.fields.make && og.fields.make['nb-NO']) {
            if (og.fields.make['nb-NO'].sys.id !== makeId) {
              return false;
            }
          }

          return true;
        });
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  getModeDefaults(mode, makeId, depth = 0) {
    return getSpace().then((space) => {
      return space.getEntries({
        'content_type': 'openGraph',
        'fields.motorforumOnly[ne]': true,
        'fields.target[exists]': false,
        'fields.url[exists]': false,
        'order': '-fields.make.sys.id,fields.priority,-sys.createdAt',
        'sys.archivedAt[exists]': false
      }).catch(
        processError
      ).then((entries) => {
        const data = entries.toPlainObject();
        data.items = data.items.filter((og) => {
          if (makeId && og.fields.make && og.fields.make['nb-NO']) {
            if (og.fields.make['nb-NO'].sys.id !== makeId) {
              return false;
            }
          } else if (!makeId && og.fields.make && og.fields.make['nb-NO']) {
            // We did not want a make specific, so filter out those
            return false;
          }

          if (mode !== undefined) {
            if (mode) {
              if (!og.fields.mode || og.fields.mode['nb-NO'] !== mode) {
                return false;
              }
            } else if (og.fields.mode) {
              // if mode is falsy, e.g. empty string
              // we try to find the global default (the one without a mode)
              return false;
            }
          }

          return true;
        });
        return linkResolve(space, data, depth);
      }).then((data) => {
        return data.items.map(transformer.contentfulToInternal);
      });
    });
  },

  createEmpty(makeId, targetId) {
    const og = {
      published: false,
      updated: false,
      draft: true,
      archived: false,
      content_type: 'lease',

      name: targetId || null,
      url: null,
      mode: null,
      priority: null,
      metadata: null,
      motorforumOnly: null,
      dealers: [],
      make: makeId ? {
        id: makeId
      } : null,
      target: targetId ? {
        id: targetId
      } : null,
      image: null
    };

    return og;
  }
};
